/**
 * Copyright 2014 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __extends } from "tslib";
import { alCoerceNumber, alCoerceString, alToBoolean, alToInt32, alToNumber, alToString } from '../runtime';
import { AVM1Rectangle, toAS3Rectangle } from './AVM1Rectangle';
import { toAS3Point } from './AVM1Point';
import { BlendModesMap, wrapAVM1NativeClass } from './AVM1Utils';
// import {convertToAS3Filter} from "./AVM1Filters";
import { toAwayColorTransform } from './AVM1ColorTransform';
import { toAS3Matrix } from './AVM1Matrix';
//import {BitmapImage2D as BitmapData} from "@awayjs/stage";
import { SceneImage2D as BitmapData } from '@awayjs/scene';
import { AssetLibrary, Point } from '@awayjs/core';
import { AVM1Object } from '../runtime/AVM1Object';
import { AVM1Stage } from './AVM1Stage';
import { BitmapImage2D } from '@awayjs/stage';
export function toAS3BitmapData(as2Object) {
    if (!(as2Object instanceof AVM1BitmapData)) {
        return null;
    }
    return as2Object.as3BitmapData;
}
var AVM1BitmapData = /** @class */ (function (_super) {
    __extends(AVM1BitmapData, _super);
    function AVM1BitmapData() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    AVM1BitmapData.createAVM1Class = function (context) {
        return wrapAVM1NativeClass(context, true, AVM1BitmapData, ['loadBitmap'], ['height#', 'rectangle#', 'transparent#', 'width#',
            'applyFilter', 'clone', 'colorTransform', 'compare', 'copyChannel',
            'copyPixels', 'dispose', 'draw', 'fillRect', 'floodFill',
            'generateFilterRect', 'getColorBoundsRect', 'getPixel', 'getPixel32',
            'hitTest', 'merge', 'noise', 'paletteMap', 'perlinNoise',
            'pixelDissolve', 'scroll', 'setPixel', 'setPixel32', 'threshold'
        ], null, AVM1BitmapData.prototype.avm1Constructor);
    };
    Object.defineProperty(AVM1BitmapData.prototype, "as3BitmapData", {
        //adaptee: BitmapData;
        get: function () {
            return this.adaptee;
        },
        enumerable: true,
        configurable: true
    });
    AVM1BitmapData.prototype.avm1Constructor = function (width, height, transparent, fillColor) {
        width = alToNumber(this.context, width);
        height = alToNumber(this.context, height);
        transparent = arguments.length < 3 ? true : alToBoolean(this.context, transparent);
        fillColor = arguments.length < 4 ? 0xFFFFFFFF : alToInt32(this.context, fillColor);
        var awayObject = new BitmapData(width, height, transparent, fillColor, false, AVM1Stage.avmStage.view.stage);
        this.adaptee = awayObject;
    };
    AVM1BitmapData.fromAS3BitmapData = function (context, awayObject) {
        var as2Object = new AVM1BitmapData(context);
        as2Object.alPrototype = context.globals.BitmapData.alGetPrototypeProperty();
        as2Object.adaptee = awayObject;
        return as2Object;
    };
    AVM1BitmapData.loadBitmap = function (context, symbolId) {
        symbolId = alToString(context, symbolId);
        /**
         * @todo FIXME, remove asset hack
         */
        var bundle = AssetLibrary.getBundle();
        var nss = Object.keys(bundle._assetDictionary);
        var symbol;
        for (var _i = 0, nss_1 = nss; _i < nss_1.length; _i++) {
            var ns = nss_1[_i];
            symbol = bundle.getAsset(symbolId, ns);
            if (symbol)
                break;
        }
        // REDUX verify
        if (symbol && symbol.isAsset(BitmapImage2D)) {
            var bitmapData = new AVM1BitmapData(context);
            bitmapData.adaptee = symbol;
            return bitmapData;
        }
        /*
        var symbolClass = symbol.symbolProps.symbolClass;
        var bitmapClass = context.sec.flash.display.BitmapData.axClass;
        if (symbol && (bitmapClass === symbolClass ||
                bitmapClass.dPrototype.isPrototypeOf((<any>symbolClass).dPrototype))) {
            var awayObject = constructClassFromSymbol(symbol.symbolProps, bitmapClass);
            bitmap.alPrototype = context.globals.BitmapData.alGetPrototypeProperty();
            bitmap.adaptee = awayObject;
            return bitmap;
        }*/
        console.warn('[AVM1 BitmapData] Missing bitmap:', symbolId);
        return null;
    };
    AVM1BitmapData.prototype.getHeight = function () {
        return this.adaptee.height;
    };
    AVM1BitmapData.prototype.getRectangle = function () {
        var rect = this.as3BitmapData;
        return new AVM1Rectangle(this.context, 0, 0, rect.width, rect.height);
    };
    AVM1BitmapData.prototype.getTransparent = function () {
        return this.adaptee.transparent;
    };
    AVM1BitmapData.prototype.getWidth = function () {
        return this.adaptee.width;
    };
    AVM1BitmapData.prototype.applyFilter = function (sourceBitmap, sourceRect, destPoint, filter) {
        // TODO handle incorrect arguments
        /*
        const as3BitmapData = sourceBitmap.as3BitmapData;
        const as3SourceRect = toAS3Rectangle(sourceRect);
        const as3DestPoint = toAS3Point(destPoint);*/
        // var as3Filter = convertToAS3Filter(this.context, filter);
        // todo 80pro
        //this.as3BitmapData.applyFilter(as3BitmapData, as3SourceRect, as3DestPoint, as3Filter);
        console.warn('[avm1/AVM1BitmapData] - applyFilter not implemented');
        return 0;
    };
    AVM1BitmapData.prototype.clone = function () {
        var bitmap = new AVM1BitmapData(this.context);
        bitmap.alPrototype = this.context.globals.BitmapData.alGetPrototypeProperty();
        bitmap.adaptee = this.adaptee.clone();
        return bitmap;
    };
    AVM1BitmapData.prototype.colorTransform = function (rect, colorTransform) {
        var as3Rect = toAS3Rectangle(rect);
        var as3ColorTransform = toAwayColorTransform(colorTransform);
        this.adaptee.colorTransform(as3Rect, as3ColorTransform);
    };
    AVM1BitmapData.prototype.compare = function (other) {
        if (!(other instanceof AVM1BitmapData)) {
            return false;
        }
        console.warn('[avm1/AVM1BitmapData] - compare not implemented');
        return true;
    };
    AVM1BitmapData.prototype.copyChannel = function (sourceBitmap, sourceRect, destPoint, sourceChannel, destChannel) {
        var as3BitmapData = sourceBitmap.as3BitmapData;
        var as3SourceRect = toAS3Rectangle(sourceRect);
        var as3DestPoint = toAS3Point(destPoint);
        sourceChannel = alCoerceNumber(this.context, sourceChannel);
        destChannel = alCoerceNumber(this.context, destChannel);
        this.as3BitmapData.copyChannel(as3BitmapData, as3SourceRect, as3DestPoint, sourceChannel, destChannel);
    };
    AVM1BitmapData.prototype.copyPixels = function (sourceBitmap, sourceRect, destPoint, alphaBitmap, alphaPoint, mergeAlpha) {
        if (!sourceBitmap) {
            console.warn('[AVM1BitmapData::copyPixels] Empty source!');
            return;
        }
        //console.warn('[avm1/AVM1BitmapData] - copyPixels not implemented');
        var thisAs3 = this.as3BitmapData;
        var as3BitmapData = sourceBitmap.as3BitmapData;
        var as3SourceRect = toAS3Rectangle(sourceRect);
        var as3DestPoint = toAS3Point(destPoint);
        var as3AlphaData = alphaBitmap ? alphaBitmap.as3BitmapData : null;
        var as3AlphaPoint = alphaPoint ? toAS3Point(alphaPoint) : null;
        mergeAlpha = alToBoolean(this.context, mergeAlpha);
        thisAs3.copyPixels(as3BitmapData, as3SourceRect, as3DestPoint, as3AlphaData, as3AlphaPoint, mergeAlpha);
    };
    AVM1BitmapData.prototype.dispose = function () {
        this.as3BitmapData.dispose();
    };
    AVM1BitmapData.prototype.draw = function (source, matrix, colorTransform, blendMode, clipRect, smooth) {
        if (!source) {
            console.warn('[AVM1BitmapData::draw] Empty source!');
            return;
        }
        var avm1Object = source;
        if (typeof source === 'string') {
            avm1Object = this.context.resolveTarget(source);
            if (!avm1Object) {
                console.warn("[AVM1BitmapData::draw] Source " + source + "  not resolved!");
                return;
            }
        }
        var as3BitmapData = avm1Object.adaptee; // movies and bitmaps
        var as3Matrix = matrix ? toAS3Matrix(matrix) : null;
        var as3ColorTransform = colorTransform ? toAwayColorTransform(colorTransform) : null;
        var as3ClipRect = clipRect ? toAS3Rectangle(clipRect) : null;
        blendMode = typeof blendMode === 'number' ? BlendModesMap[blendMode] : alCoerceString(this.context, blendMode);
        blendMode = blendMode || null;
        smooth = alToBoolean(this.context, smooth);
        //this.as3BitmapData.fillRect(this.as3BitmapData.rect, 0xffffffff);
        // IMPORTANT! Preventing unregister object after remove from parent.
        avm1Object._locked = true;
        this.as3BitmapData.draw(as3BitmapData, as3Matrix, as3ColorTransform, blendMode, as3ClipRect, smooth);
        avm1Object._locked = false;
    };
    AVM1BitmapData.prototype.fillRect = function (rect, color) {
        var as3Rect = toAS3Rectangle(rect);
        color = alToInt32(this.context, color);
        this.as3BitmapData.fillRect(as3Rect, color);
    };
    AVM1BitmapData.prototype.floodFill = function (x, y, color) {
        x = alCoerceNumber(this.context, x);
        y = alCoerceNumber(this.context, y);
        color = alToInt32(this.context, color);
        // todo 80pro
        console.warn('[avm1/AVM1BitmapData] - floodFill not implemented');
        //this.adaptee.floodFill(x, y, color);
    };
    AVM1BitmapData.prototype.generateFilterRect = function (sourceRect, filter) {
        console.warn('[avm1/AVM1BitmapData] - generateFilterRect not implemented');
        return undefined;
    };
    AVM1BitmapData.prototype.getColorBoundsRect = function (mask, color, findColor) {
        var _a;
        mask = alToInt32(this.context, mask) >>> 0;
        color = alToInt32(this.context, color) >>> 0;
        findColor = alToBoolean(this.context, findColor);
        // todo 80pro
        //console.warn('[avm1/AVM1BitmapData] - getColorBoundsRect not implemented');
        var rect = (_a = this.as3BitmapData) === null || _a === void 0 ? void 0 : _a.getColorBoundsRect(mask, color, findColor);
        return rect
            ? new AVM1Rectangle(this.context, rect.x, rect.y, rect.width, rect.height)
            : null;
        //return null;
    };
    AVM1BitmapData.prototype.getPixel = function (x, y) {
        return this.adaptee.getPixel(x, y);
    };
    AVM1BitmapData.prototype.getPixel32 = function (x, y) {
        return this.adaptee.getPixel32(x, y);
    };
    AVM1BitmapData.prototype.hitTest = function (firstPoint, firstAlphaThreshold, secondObject, secondBitmapPoint, secondAlphaThreshold) {
        //const as3FirstPoint = toAS3Point(firstPoint);
        //firstAlphaThreshold = alToInt32(this.context, firstAlphaThreshold);
        console.warn('[avm1/AVM1BitmapData] - hitTest not implemented');
        return false;
        /*
        // TODO: Check for Rectangle, Point, Bitmap, or BitmapData here. Or whatever AVM1 allows.
        var as3SecondObject = (<any>secondObject).adaptee; // movies and bitmaps
        if (arguments.length < 4) {
            return this.adaptee.hitTest(as3FirstPoint, firstAlphaThreshold, as3SecondObject);
        }
        var as3SecondBitmapPoint = secondBitmapPoint != null ? toAS3Point(secondBitmapPoint) : null;
        if (arguments.length < 4) {
            return this.adaptee.hitTest(as3FirstPoint, firstAlphaThreshold, as3SecondObject,
                as3SecondBitmapPoint);
        }
        secondAlphaThreshold = alToInt32(this.context, secondAlphaThreshold);
        */
    };
    AVM1BitmapData.prototype.merge = function (sourceBitmap, sourceRect, destPoint, redMult, greenMult, blueMult, alphaMult) {
        var as3BitmapData = sourceBitmap.as3BitmapData;
        var as3SourceRect = toAS3Rectangle(sourceRect);
        var as3DestPoint = toAS3Point(destPoint);
        redMult = alToInt32(this.context, redMult);
        greenMult = alToInt32(this.context, greenMult);
        blueMult = alToInt32(this.context, blueMult);
        alphaMult = alToInt32(this.context, alphaMult);
        this.as3BitmapData.merge(as3BitmapData, as3SourceRect, as3DestPoint, redMult, greenMult, blueMult, alphaMult);
    };
    AVM1BitmapData.prototype.noise = function (randomSeed, low, high, channelOptions, grayScale) {
        randomSeed = alToInt32(this.context, randomSeed);
        low = arguments.length < 2 ? 0 : alToInt32(this.context, low);
        high = arguments.length < 3 ? 255 : alToInt32(this.context, high);
        channelOptions = arguments.length < 4 ? 1 | 2 | 4 : alToInt32(this.context, channelOptions);
        grayScale = arguments.length < 5 ? false : alToBoolean(this.context, grayScale);
        console.warn('[avm1/AVM1BitmapData] - noise not implemented');
    };
    AVM1BitmapData.prototype.paletteMap = function (sourceBitmap, sourceRect, destPoint, redArray, greenArray, blueArray, alphaArray) {
        console.warn('[avm1/AVM1BitmapData] - paletteMap not implemented');
    };
    AVM1BitmapData.prototype.perlinNoise = function (baseX, baseY, numOctaves, randomSeed, stitch, fractalNoise, channelOptions, grayScale, offsets) {
        baseX = alCoerceNumber(this.context, baseX);
        baseY = alCoerceNumber(this.context, baseY);
        numOctaves = alCoerceNumber(this.context, numOctaves);
        randomSeed = alCoerceNumber(this.context, randomSeed);
        stitch = alToBoolean(this.context, stitch);
        fractalNoise = alToBoolean(this.context, fractalNoise);
        channelOptions = channelOptions === undefined ? 7 : alCoerceNumber(this.context, channelOptions);
        grayScale = alToBoolean(this.context, grayScale);
        /*const as3Offsets = isNullOrUndefined(offsets) ?
            null : AVM1ArrayNative.mapToJSArray(offsets, (item) => alCoerceNumber(this.context, item), this);*/
        console.warn('[avm1/AVM1BitmapData] - perlinNoise not implemented');
    };
    AVM1BitmapData.prototype.pixelDissolve = function (sourceBitmap, sourceRect, destPoint, randomSeed, numberOfPixels, fillColor) {
        //const as3BitmapData = sourceBitmap.as3BitmapData;
        var as3SourceRect = toAS3Rectangle(sourceRect);
        //const as3DestPoint = toAS3Point(destPoint);
        randomSeed = arguments.length < 4 ? 0 : alToInt32(this.context, randomSeed);
        numberOfPixels = arguments.length < 5 ?
            as3SourceRect.width * as3SourceRect.height / 30 :
            alToInt32(this.context, numberOfPixels);
        fillColor = arguments.length < 6 ? 0 : alToInt32(this.context, fillColor);
        console.warn('[avm1/AVM1BitmapData] - pixelDissolve not implemented');
        return 0;
    };
    AVM1BitmapData.prototype.scroll = function (x, y) {
        x = alCoerceNumber(this.context, x) | 0;
        y = alCoerceNumber(this.context, y) | 0;
        if (!x && !y)
            return;
        var as3this = this.as3BitmapData;
        as3this.copyPixels(as3this, as3this.rect, new Point(x, y), null, null, false);
        //console.warn('[avm1/AVM1BitmapData] - scroll not implemented');
    };
    AVM1BitmapData.prototype.setPixel = function (x, y, color) {
        x = alCoerceNumber(this.context, x);
        y = alCoerceNumber(this.context, y);
        color = alToInt32(this.context, color);
        this.adaptee.setPixel(x, y, color);
    };
    AVM1BitmapData.prototype.setPixel32 = function (x, y, color) {
        x = alCoerceNumber(this.context, x);
        y = alCoerceNumber(this.context, y);
        color = alToInt32(this.context, color);
        this.adaptee.setPixel32(x, y, color);
    };
    AVM1BitmapData.prototype.threshold = function (sourceBitmap, sourceRect, destPoint, operation, threshold, color, mask, copySource) {
        var thisAsBitmap = this.as3BitmapData;
        var as3BitmapData = sourceBitmap.as3BitmapData;
        if (!as3BitmapData) {
            return;
        }
        var as3SourceRect = toAS3Rectangle(sourceRect);
        var as3DestPoint = toAS3Point(destPoint);
        operation = alCoerceString(this.context, operation);
        threshold = alToInt32(this.context, threshold) >>> 0;
        color = arguments.length < 6 ? 0 : alToInt32(this.context, color);
        mask = arguments.length < 7 ? 0xFFFFFFFF : alToInt32(this.context, mask);
        copySource = arguments.length < 8 ? false : alToBoolean(this.context, copySource);
        // if 0, treshold is bugged
        color = color === 0 ? 0x00010101 : color;
        thisAsBitmap.threshold(as3BitmapData, as3SourceRect, as3DestPoint, operation, threshold, 0x00ff00ff, mask, copySource);
        //console.warn('[avm1/AVM1BitmapData] - scroll not implemented');
        return 0;
    };
    return AVM1BitmapData;
}(AVM1Object));
export { AVM1BitmapData };
